'use client';

import clsx from 'clsx';
import type { FC } from 'react';
import type { CardProps } from '@/types/card';
import { SafeHtml } from '@headstartwp/core/react';
import { Label } from '@/components/raven/ui/Label';
import { getIconComponent } from '@/utils/get-icons';
import { decodeEntities } from '@/utils/post-helpers';
import { VideoPlayer } from '@/components/VideoPlayer';
import { InternalLink } from '@/components/InternalLink';
import { ExternalVideo } from '@/components/ExternalVideo';
import { useAppSettings } from '@/context/AppSettingsContext';
import type { WPVideo, AnalyticsData } from '@/types/entities';
import { imageSizes } from '@/components/raven/Card/image-sizes';
import { ArticleFeaturedImage } from '@/components/ArticleFeaturedImage';
import {
	convertAspectRatio,
	convertAspectRatioToCustomProp,
} from '@/utils/convert-aspect-ratio';

import styles from './styles.module.css';

const DEFAULT_IMAGE_ASPECT_RATIO_IS_STACKED_LARGE = '9-16';
const DEFAULT_IMAGE_ASPECT_RATIO_IS_POSTER = '3-4';
const DEFAULT_IMAGE_ASPECT_RATIO_IS_CATEGORY_CARD = '1-1';
const DEFAULT_IMAGE_ASPECT_RATIO_IS_STACKED = '4-3';

// TODO: Seperate into seperate component to be re-used e.g. Hero Component
const MediaPlayer: FC<{
	analyticsData?: AnalyticsData;
	video: WPVideo;
}> = ({ analyticsData, video }) => {
	if (video.type === 'jwplayer') {
		return (
			<VideoPlayer
				analyticsData={analyticsData ?? {}}
				aspectRatio="16:9"
				bottomSpacing={false}
				mode="cover"
				options={{
					controls: false,
					mute: true,
					repeat: true,
				}}
				simpleControls
				videoId={video.platform_id}
				videoImage={video.thumbnail_url}
				videoSrc={video.videoSrc}
				videoTitle={video.video_title}
			/>
		);
	}

	return <ExternalVideo url={video.embed_url} />;
};

export const Card: FC<CardProps> = ({
	addStroke,
	align,
	aspectRatio,
	author,
	button,
	category,
	centerContent,
	className,
	date,
	description,
	displayDescription,
	image,
	isCarousel = false,
	link,
	listIndex,
	mediaType = 'image',
	meta = {},
	opensInNewTab = false,
	sizeOverride,
	tabIndex,
	title,
	titleTag = 'h2',
	variation,
	video,
}) => {
	const { settings } = useAppSettings();

	const TitleTag = titleTag;
	const isImage = mediaType === 'image';
	const isVideo = Boolean(
		video && 'videoSrc' in video && video.videoSrc,
	) as boolean;
	const isMedia = !variation.includes('list-no-media');
	const is5050 = variation.includes('50-50');
	const isContentCenter = centerContent ?? false;
	const isFullBleedMedia = variation.includes('full-bleed-media');
	const isList = variation.includes('list');
	const isStacked = variation.includes('stacked');
	const isStackedLarge = variation === 'stacked-large';
	const showDescription = description && displayDescription;
	const isCategoryCard = variation === 'category-card';
	const isPoster = variation === 'poster';
	const sizes = isMedia
		? sizeOverride || imageSizes[variation as keyof typeof imageSizes]
		: '';

	const getImageAspectRatio = () => {
		if (aspectRatio) {
			return aspectRatio;
		}

		if (isStackedLarge) {
			return DEFAULT_IMAGE_ASPECT_RATIO_IS_STACKED_LARGE;
		}

		if (isPoster) {
			return DEFAULT_IMAGE_ASPECT_RATIO_IS_POSTER;
		}

		if (isCategoryCard) {
			return DEFAULT_IMAGE_ASPECT_RATIO_IS_CATEGORY_CARD;
		}

		if (isStacked || is5050) {
			return (
				settings?.images?.card_stacked_variation_image_aspect_ratio ||
				DEFAULT_IMAGE_ASPECT_RATIO_IS_STACKED
			);
		}

		return '';
	};

	const imageAspectRatio = getImageAspectRatio();

	const categoryLabelVariant =
		is5050 || isFullBleedMedia ? 'filled' : undefined;

	const imageWrapperCx = clsx(styles.cardMedia, 'card__media', {
		[styles.cardMediaPoster]: isPoster,
		[styles.cardMediaStroke]: addStroke,
	});

	const isSponsored = meta?.is_sponsored || false;

	return (
		<div
			className={clsx(styles.card, className, 'card', `card--${variation}`, {
				[styles.categoryCard]: isCategoryCard,
				[styles.contentCard]: variation === 'content-card',
				[styles.contentCenter]: isContentCenter,
				[styles.fiftyFifty]: is5050,
				[styles.fullBleedMedia]: isFullBleedMedia,
				[styles.list]: isList,
				[styles.listHorizontal]: variation === 'list-horizontal',
				[styles.listMediaLarge]: variation?.includes('list-media-large'),
				[styles.listMediaSmall]: variation?.includes('list-media-small'),
				[styles.listNumbered]:
					variation?.includes('numbered-list-contained') ||
					variation?.includes('numbered-list-offset'),
				[styles.listNumberedContained]: variation?.includes(
					'numbered-list-contained',
				),
				[styles.listNumberedOffset]: variation?.includes(
					'numbered-list-offset',
				),
				[styles.mediaBottom]: variation.includes('media-bottom'),
				[styles.mediaLeft]: variation.includes('media-left'),
				[styles.mediaRight]: variation.includes('media-right'),
				[styles.mediaTop]: variation.includes('media-top'),
				[styles.overlay]: variation.includes('overlay'),
				[styles.overlayHero]: variation === 'overlay-hero',
				[styles.poster]: isPoster,
				[styles.stacked]: isStacked,
				[styles.stackedLarge]: isStackedLarge,
			})}
			data-testid="article-card"
		>
			{listIndex &&
				(variation.includes('numbered-list-contained') ||
					variation.includes('numbered-list-offset')) && (
					<span className={styles.cardListIndex}>
						{`${listIndex.toString().padStart(2, '0')}`}
					</span>
				)}
			{isMedia && isImage && image && (
				<figure
					className={imageWrapperCx}
					style={
						{
							'--aspect-ratio':
								convertAspectRatioToCustomProp(imageAspectRatio),
						} as React.CSSProperties
					}
				>
					<InternalLink
						className={clsx(styles.cardMediaLink, 'no-tab-focus')}
						href={link}
						tabIndex={-1}
						target={opensInNewTab ? '_blank' : '_self'}
					>
						<span className="screen-reader-text">
							{`View post: ${decodeEntities(title)}`}
						</span>
					</InternalLink>
					{isImage && !isVideo && image && (
						<>
							<ArticleFeaturedImage
								alt={image.alt_text ?? ''}
								aspectRatio={convertAspectRatio(imageAspectRatio)}
								className={clsx(styles.cardImage, 'card__image')}
								fill
								focalX={image.meta?.focal_point?.x}
								focalY={image.meta?.focal_point?.y}
								height={image?.media_details?.height ?? null}
								sizes={sizes}
								src={image.source_url}
								width={image?.media_details?.width ?? null}
							/>
							{isPoster && <div className={styles.posterScrim} />}
						</>
					)}
					{isVideo && video && (
						<div className={styles.cardVideo}>
							<MediaPlayer video={video} />
						</div>
					)}
				</figure>
			)}

			<div
				className={clsx(styles.cardContent, 'card__content', {
					[styles.cardContentIsVideo]: isVideo,
					[styles.cardContentPadding]: isCarousel,
					[styles.cardContentPoster]: isPoster,
				})}
			>
				{(category || isSponsored || isVideo) && !isPoster && (
					<div className={clsx(styles.cardCategory, 'card__category')}>
						{isSponsored && <Label text="Sponsored" variant="informational" />}
						{category && (
							<Label
								text={decodeEntities(category)}
								variant={categoryLabelVariant}
							/>
						)}
						{isVideo && <Label text="Video" variant={categoryLabelVariant} />}
					</div>
				)}
				<TitleTag
					className={clsx(styles.cardTitle, 'card__title', {
						[styles.alignLeft]: align === 'left',
					})}
				>
					<InternalLink
						className={styles.cardTitleLink}
						href={link}
						tabIndex={tabIndex}
						target={opensInNewTab ? '_blank' : '_self'}
					>
						<SafeHtml html={decodeEntities(title)} />
					</InternalLink>
				</TitleTag>

				{showDescription && description && (
					<p className={styles.cardDescription}>
						<SafeHtml html={description} />
					</p>
				)}

				{author && !isPoster && (
					<div className={styles.cardMetadata}>
						{author && (
							<p className={styles.cardContributor}>{decodeEntities(author)}</p>
						)}

						{/*
						Removed as part of WOR-4093
						{date && (
							<time className={styles.cardDate} dateTime={date}>
								{formatReadableDate(date, {
									timeZone: appSettings.settings.timezone,
								})}
							</time>
						)}
						*/}
					</div>
				)}
				{button && (button.label || button.icon) && (
					<span
						className={clsx(styles.cardButton, 'button', {
							[`button--${button.variation}`]: button.variation,
							'button--secondary': is5050,
							'button--tertiary': !is5050 && !button.variation,
						})}
					>
						{button.label && button.label}
						{button.icon && getIconComponent(button.icon, 16)}
					</span>
				)}
			</div>
		</div>
	);
};
